import { useMemo, useState } from 'react';
import Chip from '@/components/ui/Chip';
import EstadoVazio from '@/components/ui/EstadoVazio';
import Botao from '@/components/ui/Botao';
import ModalCaixa from './ModalCaixa';
import { descricaoCaixa } from './utils';
import type { CaixaDoEvento, TerminalCaixa } from './types';

interface Props {
  eventoId: number;
  caixas: CaixaDoEvento[];
  terminais: TerminalCaixa[];
  carregando: boolean;
  aoRecarregar: () => void;
}

function Tile({ valor, legenda }: { valor: number; legenda: string }) {
  return (
    <div className="flex items-baseline gap-1.5 rounded-xl bg-background-100 px-3 py-1.5">
      <span className="text-[15px] font-extrabold text-foreground-950 tabular-nums">
        {valor.toLocaleString('pt-BR')}
      </span>
      <span className="text-[12px] text-foreground-500">{legenda}</span>
    </div>
  );
}

export default function ListaCaixas({ eventoId, caixas, terminais, carregando, aoRecarregar }: Props) {
  const [aberto, setAberto] = useState<{ caixa: CaixaDoEvento | null } | null>(null);

  const apelidoPorTerminal = useMemo(
    () => new Map(terminais.map((t) => [t.terminal_externo, t.apelido])),
    [terminais],
  );

  return (
    <div className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <div className="flex items-center gap-2 px-1">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
          <i className="ri-cash-line" />
        </span>
        <h3 className="text-[17px] font-bold text-foreground-950">Caixas</h3>
      </div>
      <p className="mt-1 px-1 text-[14px] text-foreground-500">
        Cada caixa junta uma maquininha, as fichas e quem atende.
      </p>

      <div className="mt-3">
        <Botao
          variante="secundario"
          tamanho="md"
          cheio
          icone="ri-add-line"
          onClick={() => setAberto({ caixa: null })}
        >
          Novo caixa
        </Botao>
      </div>

      {carregando ? (
        <div className="mt-4 space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-secondary-100" />
          ))}
        </div>
      ) : caixas.length === 0 ? (
        <div className="mt-4">
          <EstadoVazio
            icone="ri-cash-line"
            titulo="Nenhum caixa ainda"
            descricao="Crie o caixa para o operador abrir e vender fichas, cartão e dinheiro."
            acaoRotulo="Novo caixa"
            aoClicarAcao={() => setAberto({ caixa: null })}
          />
        </div>
      ) : (
        <ul className="mt-4 space-y-3">
          {caixas.map((c) => (
            <li key={c.caixa_id} className="animate-subir">
              <button
                type="button"
                onClick={() => setAberto({ caixa: c })}
                className={`block w-full cursor-pointer rounded-2xl border border-secondary-100 bg-background-50 p-4 text-left transition-colors hover:bg-background-100 ${
                  c.ativo ? '' : 'opacity-60'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-[16px] font-bold text-foreground-950">{c.nome}</p>
                    <p className="mt-0.5 text-[13px] text-foreground-500">
                      {descricaoCaixa(c, apelidoPorTerminal)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {!c.ativo && <Chip tipo="neutro">Desativado</Chip>}
                    <span className="flex h-5 w-5 items-center justify-center text-foreground-300">
                      <i className="ri-arrow-right-s-line text-xl" />
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Tile valor={c.operadores} legenda="operadores" />
                  <Tile valor={c.vendas_hoje} legenda="vendas hoje" />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-4 px-1 text-[13px] leading-relaxed text-foreground-500">
        O operador abre o caixa em <strong className="font-semibold">Caixa</strong> (menu) e escolhe
        o caixa dele. Cartão vai para a maquininha; as fichas saem impressas.
      </p>

      {aberto && (
        <ModalCaixa
          eventoId={eventoId}
          caixa={aberto.caixa}
          terminais={terminais}
          aoFechar={() => setAberto(null)}
          aoSalvo={aoRecarregar}
        />
      )}
    </div>
  );
}